import {
  CalendarDay,
  addCalendarDays,
  addCalendarMonths,
  calendarDayOf,
  calendarDayParts,
  startOfIsoCalendarWeek,
} from '@shared/models/dateTypes';

import { PeriodValue } from './filter-state';

export interface PeriodShortcut {
  id: string;
  label: string;
  range(today: CalendarDay): PeriodValue;
}

/** The six shortcuts in the period panel, in the design's order (R5). "I dag" is Danish today. */
export const PERIOD_SHORTCUTS: readonly PeriodShortcut[] = [
  { id: 'today', label: 'I dag', range: (t) => ({ from: t, to: t }) },
  {
    id: 'today-tomorrow',
    label: 'I dag og i morgen',
    range: (t) => ({ from: t, to: addCalendarDays(t, 1) }),
  },
  {
    id: 'this-week',
    label: 'Denne uge',
    range: (t) => {
      const monday = startOfIsoCalendarWeek(t);
      return { from: monday, to: addCalendarDays(monday, 6) };
    },
  },
  { id: 'next-7', label: 'Næste 7 dage', range: (t) => ({ from: t, to: addCalendarDays(t, 6) }) },
  {
    id: 'this-month',
    label: 'Denne måned',
    range: (t) => {
      const { year, month } = calendarDayParts(t);
      const first = calendarDayOf(year, month, 1);
      return { from: first, to: addCalendarDays(addCalendarMonths(first, 1), -1) };
    },
  },
  {
    id: 'next-30',
    label: 'Næste 30 dage',
    range: (t) => ({ from: t, to: addCalendarDays(t, 29) }),
  },
];

/**
 * The six shortcuts for a period that lies behind us — when something was created or approved
 * (#861: nobody is created tomorrow). Each ends today.
 */
export const PAST_PERIOD_SHORTCUTS: readonly PeriodShortcut[] = [
  { id: 'today', label: 'I dag', range: (t) => ({ from: t, to: t }) },
  {
    id: 'yesterday-today',
    label: 'I går og i dag',
    range: (t) => ({ from: addCalendarDays(t, -1), to: t }),
  },
  {
    id: 'this-week-so-far',
    label: 'Denne uge',
    range: (t) => ({ from: startOfIsoCalendarWeek(t), to: t }),
  },
  { id: 'last-7', label: 'Sidste 7 dage', range: (t) => ({ from: addCalendarDays(t, -6), to: t }) },
  {
    id: 'this-month-so-far',
    label: 'Denne måned',
    range: (t) => {
      const { year, month } = calendarDayParts(t);
      return { from: calendarDayOf(year, month, 1), to: t };
    },
  },
  {
    id: 'last-30',
    label: 'Sidste 30 dage',
    range: (t) => ({ from: addCalendarDays(t, -29), to: t }),
  },
];

/** The shortcut a period equals today, if any — the chip then says "Denne uge" rather than two dates. */
export function matchShortcut(
  period: PeriodValue,
  today: CalendarDay,
  shortcuts: readonly PeriodShortcut[] = PERIOD_SHORTCUTS,
): PeriodShortcut | undefined {
  return shortcuts.find((s) => {
    const r = s.range(today);
    return r.from === period.from && r.to === period.to;
  });
}

const dmy = (day: CalendarDay) => {
  const { year, month, date } = calendarDayParts(day);
  return `${String(date).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
};

/**
 * The chip's value text: a shortcut's name, "01-10-2026 – 31-10-2026", "Fra …", "Til …", or
 * {@link anyLabel} for no dates at all ("Alle", "Når som helst").
 */
export function periodText(
  period: PeriodValue,
  today: CalendarDay,
  anyLabel = 'Alle',
  shortcuts: readonly PeriodShortcut[] = PERIOD_SHORTCUTS,
): string {
  const shortcut = matchShortcut(period, today, shortcuts);
  if (shortcut) return shortcut.label;
  if (period.from && period.to)
    return period.from === period.to ? dmy(period.from) : `${dmy(period.from)} – ${dmy(period.to)}`;
  if (period.from) return `Fra ${dmy(period.from)}`;
  if (period.to) return `Til ${dmy(period.to)}`;
  return anyLabel;
}
